import { useQuery } from '@tanstack/react-query';

import { adUnitsService } from '@/services/data/AdUnitsService';

/**
 * The declared set of ad-copy platforms, read from `public.ad_copy_platforms`
 * (`AdUnitsService.listAdCopyPlatforms` — see its doc comment for why this
 * table, not a literal array, is the source of truth). A global reference
 * table, not account-scoped: no owner resolution, one shared cache entry for
 * every signed-in user, `staleTime: Infinity` because the set only changes via
 * a migration, never at runtime.
 *
 * A platform filter (or any other UI that needs "which ad platforms exist")
 * should read this hook rather than hardcoding `['meta', 'linkedin',
 * 'google_rsa']` — a hardcoded list silently stops matching the database the
 * next time a platform is added or retired here.
 *
 * 🔴 `staleTime: Infinity` PLUS the app's 24h IndexedDB query persistence
 * (`persistOptions` in `src/config/queryClient.ts`) means a browser that
 * cached this query BEFORE a migration flips one of these rows will keep
 * serving the stale snapshot for up to 24h after — `refetchOnMount` is a
 * no-op once `staleTime` is `Infinity`, and nothing else invalidates this
 * key. Reproduced live 2026-09-24 (Lane R3-1 repair 1): a browser that had
 * cached `linkedin.supportsCopyRevision: false` before
 * `20260924210000_linkedin_copy_revision_capability.sql` ran kept showing
 * the stale amber "can't be made current" chip on `/briefs/:id` after the
 * flip, on a byte-for-byte fresh page load — no code bug in the chip itself,
 * a stale-persisted-cache bug in this query. The BUMP below (`2`) forces
 * every already-cached browser onto a fresh key, so this one flip is visible
 * everywhere immediately. Bump it again (`3`, `4`, ...) the next time this
 * table's data changes via a migration — see `queryKeys.workflows.templates`
 * for the same pattern already used elsewhere in this file's sibling
 * `queryClient.ts`.
 */
export function useAdCopyPlatforms() {
  return useQuery({
    queryKey: ['ad-copy-platforms', 2],
    queryFn: () => adUnitsService.listAdCopyPlatforms(),
    staleTime: Infinity,
  });
}
